<template>
  <div class="coupon-manage">
    <div class="page-header">
      <h2>优惠券管理</h2>
      <el-button
        type="primary"
        class="distribute-btn"
        @click="showDistributeDialog"
      >
        <el-icon><Plus /></el-icon>
        批量发放优惠券
      </el-button>
    </div>

    <!-- 批量发放优惠券弹窗 -->
    <el-dialog
      title="批量发放优惠券"
      v-model="dialogVisible"
      width="50%"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @open="handleDialogOpen"
      @closed="handleDialogClosed"
      class="distribute-dialog"
    >
      <el-form
        :model="distributeCoupon"
        :rules="distributeRules"
        ref="distributeForm"
        label-width="120px"
        class="distribute-form"
      >
        <el-form-item label="优惠类型" prop="type">
          <el-select
            v-model="distributeCoupon.type"
            placeholder="请选择优惠类型"
            class="form-select"
          >
            <el-option label="固定金额" value="fixed"></el-option>
            <el-option label="百分比折扣" value="percentage"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="优惠值" prop="value">
          <div class="value-input-group">
            <el-input-number
              v-model="distributeCoupon.value"
              :min="0"
              :max="distributeCoupon.type === 'percentage' ? 100 : 1000"
              :precision="2"
              class="value-input"
            >
            </el-input-number>
            <span class="unit">{{
              distributeCoupon.type === "percentage" ? "%" : "元"
            }}</span>
            <span
              v-if="distributeCoupon.type === 'percentage'"
              class="discount-tip"
            >
              ({{ (distributeCoupon.value / 10).toFixed(1) }}折)
            </span>
          </div>
        </el-form-item>
        <el-form-item label="最低使用金额" prop="min_amount">
          <div class="value-input-group">
            <el-input-number
              v-model="distributeCoupon.min_amount"
              :min="0"
              :precision="2"
              class="value-input"
            ></el-input-number>
            <span class="unit">元</span>
          </div>
        </el-form-item>
        <el-form-item label="有效期" prop="dateRange">
          <el-date-picker
            v-model="distributeCoupon.dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD HH:mm:ss"
            format="YYYY-MM-DD HH:mm:ss"
            class="date-picker"
          >
          </el-date-picker>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeDialog">取 消</el-button>
          <el-button type="primary" @click="distributeToAll">确 定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 优惠券列表 -->
    <el-card class="coupon-list-card">
      <div class="card-header">
        <h3>优惠券列表</h3>
        <!-- 筛选条件 -->
        <div class="filter-container">
          <el-select
            v-model="filterStatus"
            placeholder="选择状态"
            clearable
            @change="handleFilterChange"
            class="status-filter"
          >
            <el-option label="未生效" value="pending"></el-option>
            <el-option label="可使用" value="active"></el-option>
            <el-option label="已使用" value="used"></el-option>
            <el-option label="已过期" value="expired"></el-option>
          </el-select>
        </div>
      </div>

      <el-table
        :data="paginatedCoupons"
        style="width: 100%"
        :stripe="true"
        :border="true"
        class="coupon-table"
      >
        <el-table-column
          prop="code"
          label="优惠券码"
          width="120"
        ></el-table-column>
        <el-table-column prop="type" label="类型" width="100">
          <template #default="scope">
            {{ scope.row.type === "fixed" ? "固定金额" : "百分比折扣" }}
          </template>
        </el-table-column>
        <el-table-column prop="value" label="优惠值" width="120">
          <template #default="scope">
            <template v-if="scope.row.type === 'percentage'">
              <span class="value-text">{{ scope.row.value }}%</span>
              <span class="discount-text"
                >({{ (scope.row.value / 10).toFixed(1) }}折)</span
              >
            </template>
            <template v-else>
              <span class="value-text">{{ scope.row.value }}元</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="min_amount" label="最低使用金额" width="120">
          <template #default="scope">
            <span class="amount-text">{{ scope.row.min_amount }}元</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="start_time"
          label="开始时间"
          width="180"
        ></el-table-column>
        <el-table-column
          prop="end_time"
          label="结束时间"
          width="180"
        ></el-table-column>
        <el-table-column prop="is_used" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="getCouponStatusType(scope.row)" effect="light">
              {{ getCouponStatus(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="user_id" label="指定用户" min-width="120">
          <template #default="scope">
            {{ scope.row.user_name }}
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredCoupons.length"
          background
        >
        </el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from "axios";
import { Plus } from "@element-plus/icons-vue";

export default {
  name: "CouponManage",
  components: {
    Plus,
  },
  data() {
    return {
      distributeCoupon: {
        type: "fixed",
        value: 0,
        min_amount: 0,
        dateRange: [],
      },
      coupons: [],
      distributeRules: {
        type: [
          { required: true, message: "请选择优惠类型", trigger: "change" },
        ],
        value: [{ required: true, message: "请输入优惠值", trigger: "blur" }],
        dateRange: [
          { required: true, message: "请选择有效期", trigger: "change" },
        ],
      },
      filterStatus: "", // 状态筛选
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页显示数量
      dialogVisible: false, // 控制弹窗的显示与隐藏
      initialDistributeCoupon: {
        type: "fixed",
        value: 0,
        min_amount: 0,
        dateRange: [],
      },
    };
  },
  computed: {
    filteredCoupons() {
      if (!this.filterStatus) {
        return this.coupons;
      }

      return this.coupons.filter((coupon) => {
        const now = new Date();
        const endTime = new Date(coupon.end_time);
        const startTime = new Date(coupon.start_time);

        switch (this.filterStatus) {
          case "used":
            return coupon.is_used;
          case "expired":
            return !coupon.is_used && endTime < now;
          case "pending":
            return !coupon.is_used && now < startTime;
          case "active":
            return !coupon.is_used && now >= startTime && now <= endTime;
          default:
            return true;
        }
      });
    },
    paginatedCoupons() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredCoupons.slice(start, end);
    },
  },
  created() {
    this.fetchCoupons();
  },
  methods: {
    async distributeToAll() {
      try {
        await this.$refs.distributeForm.validate();

        const distributeData = {
          type: this.distributeCoupon.type,
          value: this.distributeCoupon.value,
          min_amount: this.distributeCoupon.min_amount,
          start_time: this.distributeCoupon.dateRange[0],
          end_time: this.distributeCoupon.dateRange[1],
        };

        console.log("Sending data to server:", distributeData);

        const response = await axios.post(
          "https://www.xiaoliangtongxue.cn/api/coupon/distribute-all",
          distributeData
        );

        if (response.data.success) {
          this.$message.success(response.data.message);
          this.closeDialog();
          this.fetchCoupons();
        } else {
          console.error("Server response error:", response.data);
          this.$message.error(response.data.message || "发放优惠券失败");
        }
      } catch (error) {
        console.error("Distribution error:", error);
        if (error.response) {
          console.error("Server error response:", error.response.data);
          this.$message.error(
            error.response.data.message || "发放优惠券失败，请检查输入数据"
          );
        } else if (error.request) {
          console.error("No response received:", error.request);
          this.$message.error("服务器无响应，请检查网络连接");
        } else {
          console.error("Request error:", error.message);
          this.$message.error("发送请求失败，请稍后重试");
        }
      }
    },
    async fetchCoupons() {
      try {
        const response = await axios.get("https://www.xiaoliangtongxue.cn/api/coupon");
        // Ensure response.data is an array
        this.coupons = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error fetching coupons:", error);
        this.$message.error("获取优惠券列表失败");
        this.coupons = [];
      }
    },
    showDistributeDialog() {
      this.dialogVisible = true;
    },
    closeDialog() {
      this.dialogVisible = false;
    },
    handleDialogOpen() {
      // 重置表单数据
      this.distributeCoupon = JSON.parse(
        JSON.stringify(this.initialDistributeCoupon)
      );
      // 在下一个 tick 重置表单验证状态
      this.$nextTick(() => {
        if (this.$refs.distributeForm) {
          this.$refs.distributeForm.resetFields();
        }
      });
    },
    handleDialogClosed() {
      // 清理表单状态
      if (this.$refs.distributeForm) {
        this.$refs.distributeForm.resetFields();
      }
    },
    resetDistributeForm() {
      // 重置为初始状态
      this.distributeCoupon = JSON.parse(
        JSON.stringify(this.initialDistributeCoupon)
      );
      if (this.$refs.distributeForm) {
        this.$refs.distributeForm.resetFields();
      }
    },

    getCouponStatus(coupon) {
      if (coupon.is_used) {
        return "已使用";
      }
      const now = new Date();
      const endTime = new Date(coupon.end_time);
      const startTime = new Date(coupon.start_time);

      if (now < startTime) {
        return "未生效";
      }
      if (endTime < now) {
        return "已过期";
      }
      return "可使用";
    },

    getCouponStatusType(coupon) {
      if (coupon.is_used) {
        return "info";
      }
      const now = new Date();
      const endTime = new Date(coupon.end_time);
      const startTime = new Date(coupon.start_time);

      if (now < startTime) {
        return "warning";
      }
      if (endTime < now) {
        return "danger";
      }
      return "success";
    },

    handleFilterChange() {
      this.currentPage = 1; // 重置页码
    },

    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
    },

    handleCurrentChange(val) {
      this.currentPage = val;
    },
  },
};
</script>

<style scoped>
.coupon-manage {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 48px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

h2 {
  margin: 0;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}

.distribute-btn {
  font-weight: 500;
}

.distribute-btn :deep(.el-icon) {
  margin-right: 4px;
}

.coupon-list-card {
  background: #fff;
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 500;
}

.filter-container {
  display: flex;
  gap: 16px;
}

.status-filter {
  width: 160px;
}

.distribute-dialog :deep(.el-dialog__body) {
  padding: 20px 40px;
}

.distribute-form {
  margin: 0;
}

.form-select,
.date-picker {
  width: 100%;
}

.value-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-input {
  width: 160px;
}

.unit {
  color: #606266;
  font-size: 14px;
}

.discount-tip {
  color: #909399;
  font-size: 13px;
}

.coupon-table {
  margin: 16px 0;
}

.coupon-table :deep(.el-table__header) {
  background-color: #f5f7fa;
}

.value-text,
.amount-text {
  color: #606266;
  font-weight: 500;
}

.discount-text {
  color: #909399;
  font-size: 13px;
  margin-left: 4px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 添加过渡动画 */
:deep(.el-dialog) {
  transition: all 0.3s ease-in-out;
}

:deep(.el-tag) {
  transition: all 0.2s ease;
}

:deep(.el-button) {
  transition: all 0.2s ease;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .coupon-manage {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .filter-container {
    width: 100%;
  }

  .status-filter {
    width: 100%;
  }

  .distribute-dialog {
    width: 90% !important;
  }

  .date-picker {
    width: 100%;
  }
}
</style> 